import React, { useState, useEffect, useMemo } from 'react'
import {
  formatISO,
  parseISO,
  formatDistanceToNowStrict,
  compareAsc,
  roundToNearestMinutes,
  setHours,
  setMinutes,
  format,
} from 'date-fns'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const tzAwareNow = () => formatISO(Date.now())

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () =>
      setIsMobile(window.matchMedia('(max-width: 767px)').matches)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return isMobile
}

const TzAwareDatetimePicker = ({
  id,
  label,
  val,
  setVal,
  className,
  innerStyle,
  hideDistance,
  disabled = false,
  restrictTo15 = false, // if true, time selection is restricted to 15-minute intervals
}) => {
  const isMobile = useIsMobile()

  const localDt = parseISO(val)
  const effectiveDt = restrictTo15
    ? roundToNearestMinutes(localDt, { nearestTo: 15 })
    : localDt

  const handleChange = date => {
    if (date) {
      const effectiveDate = restrictTo15
        ? roundToNearestMinutes(date, { nearestTo: 15 })
        : date
      setVal(formatISO(parseISO(formatISO(effectiveDate)))) // roundtrip to add timezone info
    } else {
      setVal('')
    }
  }

  const memoizedDatePicker = useMemo(
    () => (
      <DatePicker
        id={id}
        name={id}
        selected={effectiveDt}
        onChange={handleChange}
        showTimeInput={!restrictTo15}
        showTimeSelect={restrictTo15}
        timeFormat="HH:mm"
        dateFormat="MM/dd/yyyy HH:mm"
        timeCaption="Time"
        {...(restrictTo15 && {
          timeIntervals: 15,
          minTime: setHours(setMinutes(new Date(), 0), 0),
          maxTime: setHours(setMinutes(new Date(), 45), 23),
        })}
        className="form-control"
        style={innerStyle}
        disabled={disabled}
        aria-label={label}
      />
    ),
    [effectiveDt, restrictTo15, disabled, label],
  )

  return (
    <div className={className}>
      {label && (
        <label className="form-label" htmlFor={id}>
          {label}
        </label>
      )}
      <div>
        {isMobile ? (
          <input
            type="datetime-local"
            id={id}
            name={id}
            value={format(effectiveDt, "yyyy-MM-dd'T'HH:mm")}
            onChange={e => {
              // Roundtrip to add timezone info
              handleChange(parseISO(e.target.value))
            }}
            className="form-control"
            style={innerStyle}
            disabled={disabled}
            step={restrictTo15 ? 900 : 60}
            aria-label={label}
          />
        ) : (
          memoizedDatePicker
        )}
        {!hideDistance && val && (
          <small className="text-muted ms-3">
            {formatDistanceToNowStrict(effectiveDt)}
            {compareAsc(effectiveDt, new Date()) === -1
              ? ' ago'
              : ' in the future'}
          </small>
        )}
      </div>
    </div>
  )
}

export { TzAwareDatetimePicker, tzAwareNow }
